import { productById, type ProductId } from "../products/registry";
import { Blurred } from "./Blurred";
import { Bars, MetricTile, series } from "./charts";
import { Page, Panel } from "./ui";
import { ProductTag } from "../products/PerProduct";

/**
 * A surface that exists in the design and has no API behind it yet.
 *
 * Almost all of these are Labeeb's — beta grants, HITL gates, greeting and
 * chips, fleet evidence. Labeeb is deployed and serving merchants; it has no
 * ADMIN route table, so there is nothing here to read and nothing to write.
 *
 * They keep their place in the navigation rather than being hidden, for the
 * same reason `Gate` shows a control it cannot enable: a console whose shape
 * changes depending on what happens to be wired makes "where did that go?"
 * unanswerable, and the layout is itself information — it says what this
 * console will cover.
 *
 * The page renders the design's SHAPE, blurred, behind a coming-soon label.
 * That is deliberate and it is the only safe way to show it: an operator can
 * see a table of grants belongs here without being able to read a grant, so
 * nothing on screen can be mistaken for a real merchant, tier or figure.
 *
 * PRECISELY: no skeleton can carry a value from an API — this component is
 * forbidden from fetching (asserted in ComingSoon.test.ts) and takes no data
 * props, so every mark in it is written in this repo. Some of those marks ARE
 * numbers: the chart silhouette draws a seeded series and a computed delta, the
 * table silhouette draws bar lengths. They are invented, fixed, `aria-hidden`,
 * `select-none` and blurred — but the blur is the only thing between a reader
 * and a digit, so "carries no values" was the wrong way to say it.
 *
 * NOT the same as `NotMounted`, which says a product has not deployed to this
 * environment. That is an operational fact about a gateway; this is a
 * statement about a roadmap. Rendering one for the other would be a lie in a
 * place operators are entitled to trust.
 *
 * These pages must never fetch. A test asserts this module never imports
 * `useResource` or `useWrite`, so nobody can quietly wire one to invented
 * numbers to make a demo look complete.
 */
export type Shape = "table" | "chart" | "switches" | "editor";

export function ComingSoon({
  what,
  product,
  needs,
  blockers,
  designNote,
  shape = "table",
}: {
  what: string;
  /**
   * Whose surface this is. Given, `needs` comes from the registry — so what
   * Labeeb requires is written once and every surface waiting on it says the
   * same thing. Four pages that each phrased it slightly differently read as
   * four separate gaps rather than one.
   */
  product?: ProductId;
  /** Only for a gap no product owns — a billing surface, a usage history route. */
  needs?: string;
  /**
   * The specific endpoints or systems that must exist first, named precisely
   * enough to be a work item.
   *
   * The blurred shape above answers "what will be here"; this answers "what has
   * to happen", and only the second one is actionable. Without it a reader
   * knows a grants table is coming and has no idea whether that means a
   * fortnight or another team's roadmap.
   */
  blockers?: { label: string; detail: string }[];
  /**
   * What the design specifies about the surface, so the intent survives the
   * wait. When this is finally built, the constraints it was designed under —
   * grants always expire, removing a gate is the dangerous direction — should
   * not have to be rediscovered from a prototype nobody can still run.
   */
  designNote?: string;
  /**
   * Which skeleton to draw. NOT data — it selects a silhouette, so a grants
   * table does not render as a bar chart. Nothing it draws is readable.
   */
  shape?: Shape;
}) {
  const p = product ? productById(product) : undefined;
  const blocker = p?.needs ?? needs ?? "an admin API";

  return (
    <Page
      title={
        <span className="flex flex-wrap items-center gap-2">
          {what}
          {product && <ProductTag id={product} />}
        </span>
      }
      subtitle="Coming soon"
    >
      <Blurred what={what} needs={blocker} minHeight={320}>
        <Skeleton shape={shape} />
      </Blurred>

      {blockers && blockers.length > 0 && (
        <Panel className="mt-6 p-5">
          <h2 className="text-sm font-semibold text-slate-950 dark:text-white">
            What this page is waiting on
          </h2>
          <div className="mt-3 divide-y divide-slate-950/5 dark:divide-white/10">
            {blockers.map((n) => (
              <div key={n.label} className="py-2.5 first:pt-0 last:pb-0">
                <p className="ref text-sm text-slate-950 dark:text-white">{n.label}</p>
                <p className="mt-0.5 text-xs/5 text-slate-500 dark:text-slate-400">{n.detail}</p>
              </div>
            ))}
          </div>
        </Panel>
      )}

      {designNote && (
        <section className="mt-4 rounded-lg bg-slate-950/[2.5%] p-5 dark:bg-white/5">
          <h2 className="text-sm font-semibold text-slate-950 dark:text-white">
            What the design specifies
          </h2>
          <p className="mt-2 text-sm/6 text-slate-600 dark:text-slate-300">{designNote}</p>
        </section>
      )}

      {/* Why the shape above is blurred rather than seeded. The design canvas
          ships sample tenants and usage series so the interaction can be
          judged, which is right for a prototype and wrong here: an operator
          cannot tell a seeded figure from a real one, and the first time they
          act on one is an incident. */}
      <p className="mt-4 text-xs/5 text-slate-500 dark:text-slate-400">
        <strong className="text-slate-950 dark:text-white">Blurred rather than mocked.</strong> The
        shape is the design&rsquo;s; every figure in it is unreadable on purpose, because nothing
        on screen could tell you which numbers were real.
      </p>
    </Page>
  );
}

/**
 * Silhouettes. Every mark is invented here and none comes from an API — but
 * the chart and table shapes do render numbers (a seeded series, its delta, bar
 * lengths), so what makes them safe is `Blurred`: `aria-hidden`, `select-none`
 * and a 6px blur. Not the absence of digits.
 */
function Skeleton({ shape, compact = false }: { shape: Shape; compact?: boolean }) {
  const rows = compact ? 3 : 6;

  if (shape === "chart") {
    return (
      <div className={`grid gap-4 ${compact ? "" : "sm:grid-cols-2"}`}>
        {[0, 1].slice(0, compact ? 1 : 2).map((i) => (
          <Panel key={i} className="p-4">
            <MetricTile
              label="————"
              value="———"
              note="————"
              values={series(`skeleton-${shape}-${i}`, 30, 40)}
              from="——"
              to="——"
            />
          </Panel>
        ))}
      </div>
    );
  }

  if (shape === "switches") {
    return (
      <Panel className="p-4">
        <div className="flex flex-col gap-4">
          {Array.from({ length: rows }, (_, i) => (
            <div key={i} className="flex items-center justify-between gap-4">
              <div className="flex-1">
                <div className="h-3 w-40 rounded bg-slate-950/10 dark:bg-white/10" />
                <div className="mt-1.5 h-2 w-64 rounded bg-slate-950/5 dark:bg-white/5" />
              </div>
              <div className="h-6 w-10 rounded-full bg-slate-950/10 dark:bg-white/10" />
            </div>
          ))}
        </div>
      </Panel>
    );
  }

  if (shape === "editor") {
    return (
      <Panel className="p-4">
        <div className="flex flex-col gap-4">
          {Array.from({ length: rows }, (_, i) => (
            <div key={i}>
              <div className="h-3 w-28 rounded bg-slate-950/10 dark:bg-white/10" />
              <div className="mt-2 h-9 w-full rounded-lg bg-slate-950/5 ring-1 ring-slate-950/10 dark:bg-white/5 dark:ring-white/10" />
            </div>
          ))}
        </div>
      </Panel>
    );
  }

  // table
  return (
    <Panel className="overflow-hidden">
      <div className="bg-slate-950/[2.5%] px-4 py-2.5 dark:bg-white/5">
        <div className="h-2.5 w-24 rounded bg-slate-950/10 dark:bg-white/10" />
      </div>
      <div className="divide-y divide-slate-950/5 dark:divide-white/10">
        {Array.from({ length: rows }, (_, i) => (
          <div key={i} className="flex items-center gap-4 px-4 py-3">
            <div className="h-3 flex-1 rounded bg-slate-950/10 dark:bg-white/10" />
            <div className="h-3 w-24 rounded bg-slate-950/5 dark:bg-white/5" />
            <div className="h-5 w-16 rounded-full bg-slate-950/5 dark:bg-white/5" />
          </div>
        ))}
      </div>
      {!compact && (
        <div className="p-4">
          <Bars
            rows={[
              { label: "————", value: 8, tone: "mute" },
              { label: "———", value: 5, tone: "mute" },
              { label: "——", value: 2, tone: "mute" },
            ]}
          />
        </div>
      )}
    </Panel>
  );
}
